import { MouseEvent, useEffect, useCallback, useRef } from "react";

import Typography, { TypographyProps } from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material/styles";
import CopyAllTwoToneIcon from "@mui/icons-material/CopyAllTwoTone";

import { FormattedMessage } from "react-intl";
import { formatBinIinUi } from "shared/utils/converters";
import useSnackbar from "../hooks/useSnackbar";

type BinIinFieldProps = {
  value?: string | null;
  isIin?: boolean;
} & TypographyProps;

const CopyButton = styled(IconButton)(({ theme }) => ({
  width: theme.spacing(3),
  height: theme.spacing(3),
  marginLeft: theme.spacing(0.5),
}));

const CopyStack = styled(Stack)(({ theme }) => ({
  button: {
    visibility: "hidden",
  },

  "&:hover": {
    button: {
      visibility: "visible",
    },
  },
}));

const removeSpacesAndCopy = (str: string) => {
  const pureBin = str.replace(/\D/g, "");
  if (pureBin && pureBin.length <= 12) {
    navigator.clipboard.writeText(pureBin).then();
  }
};

const BinIinField = ({ value, isIin, ...typographyPros }: BinIinFieldProps) => {
  const fieldRef = useRef<HTMLElement | null>(null);
  const showSnackbar = useSnackbar();

  useEffect(() => {
    const clickListener = (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const copyListener = (e: ClipboardEvent) => {
      const selection = window.getSelection();
      if (selection) {
        const selectedText = selection.toString();
        removeSpacesAndCopy(selectedText);
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    };

    const currentRef = fieldRef.current;

    if (currentRef) {
      currentRef.addEventListener("copy", copyListener);
      currentRef.addEventListener("click", clickListener);
    }
    return () => {
      if (currentRef) {
        currentRef.removeEventListener("copy", copyListener);
        currentRef.removeEventListener("click", clickListener);
      }
    };
  }, []);

  const handleCopyIin = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (value) {
        removeSpacesAndCopy(value);
        showSnackbar(isIin ? "iinCopied" : "binCopied", "success");
        e.preventDefault();
        e.stopPropagation();
      }
    },
    [value, isIin, showSnackbar],
  );

  return (
    <>
      {!value && (
        <Typography {...typographyPros}>
          <FormattedMessage id='notFilledIn' />
        </Typography>
      )}
      {value && (
        <CopyStack direction='row' alignItems='center'>
          <Typography {...typographyPros} ref={fieldRef}>
            {formatBinIinUi(value)}
          </Typography>
          <Tooltip
            title={<FormattedMessage id={isIin ? "copyIin" : "copyBin"} />}
          >
            <CopyButton onClick={handleCopyIin}>
              <CopyAllTwoToneIcon fontSize='small' />
            </CopyButton>
          </Tooltip>
        </CopyStack>
      )}
    </>
  );
};

export default BinIinField;
